import type { ElementType, ReactNode } from "react";
import { cn } from "@/lib/cn";

interface Props {
  /** Lucide icon component to display */
  icon: ElementType;
  /** Bold title text */
  title: string;
  /** Optional descriptive subtitle */
  subtitle?: string;
  /**
   * Extra classes for the subtitle paragraph. Exists for full-sentence
   * subtitles, which have no measure of their own and otherwise render as one
   * very long line on a wide container — pass a `max-w-*` to cap them.
   */
  subtitleClassName?: string;
  /** Optional action element (e.g. a button) rendered below the subtitle */
  action?: ReactNode;
}

/**
 * Reusable empty-state illustration for tables, lists, and panels.
 * Renders a centered icon, title, optional subtitle, and optional action.
 */
export function EmptyState({
  icon: Icon,
  title,
  subtitle,
  subtitleClassName,
  action,
}: Props) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-50">
        <Icon className="h-7 w-7 text-gray-300" aria-hidden="true" />
      </div>
      <p className="text-sm font-semibold text-gray-500">{title}</p>
      {subtitle && (
        <p className={cn("text-xs text-gray-400", subtitleClassName)}>
          {subtitle}
        </p>
      )}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}
